/* eslint-disable max-lines-per-function --
   TODO: 招募前 sprint 拆組件 (TODO-frontend-eslint-ci-warnings-as-errors)。
   2026-02-06 規則加入時 87 個既有違規凍結為 baseline，新檔/新函數仍受 hook 攔截。
*/
// frontend/src/components/Search/SearchHistory.jsx
// 搜尋歷史元件 - 簡潔內嵌式設計
// Phase 2.2: 顯示最近搜尋和熱門搜尋

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Chip, Skeleton } from '@mui/material';
import {
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { getSearchHistory, clearSearchHistory } from '../../services/searchService';

// 自訂顏色 - 佛學意境風格
const COLORS = {
  // 最近搜尋：淡紫灰底 + 深紫文字 (禪意)
  recentBg: '#EDE7F6',
  recentText: '#5E35B1',
  recentHover: '#D1C4E9',
  // 熱門搜尋：琥珀金底 + 深棕文字 (莊嚴)
  popularBg: '#FFE082',
  popularText: '#6D4C41',
  popularHover: '#FFD54F',
};

const SearchHistory = ({ onSelectQuery, limit = 5 }) => {
  const [recentHistory, setRecentHistory] = useState([]);
  const [popularHistory, setPopularHistory] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  // 載入搜尋歷史
  useEffect(() => {
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const data = await getSearchHistory(limit);
        const history = data.history || [];
        const authenticated = data.isAuthenticated || false;

        setIsAuthenticated(authenticated);

        if (authenticated) {
          // 已登入：history 是個人歷史，另外取熱門搜尋
          setRecentHistory(history.slice(0, 5));
          // 熱門搜尋從另一個來源或使用相同資料（依搜尋次數排序）
          const sortedByCount = [...history].sort(
            (a, b) => (b.searchCount || 0) - (a.searchCount || 0)
          );
          setPopularHistory(sortedByCount.slice(0, 5));
        } else {
          // 未登入：history 是全站熱門搜尋
          setRecentHistory([]);
          setPopularHistory(history.slice(0, 5));
        }
      } catch (error) {
        console.error('載入搜尋歷史失敗:', error);
        setRecentHistory([]);
        setPopularHistory([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [limit]);

  // 處理點擊關鍵字
  const handleQueryClick = (query) => {
    if (onSelectQuery) {
      onSelectQuery(query);
    }
  };

  // 處理清除歷史
  const handleClearHistory = async () => {
    if (!isAuthenticated) return;

    setClearing(true);
    try {
      await clearSearchHistory();
      setRecentHistory([]);
    } catch (error) {
      console.error('清除歷史失敗:', error);
    } finally {
      setClearing(false);
    }
  };

  // 載入中狀態
  if (loading) {
    return (
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="text" width={80} />
          <Skeleton variant="rounded" width={60} height={24} />
          <Skeleton variant="rounded" width={60} height={24} />
          <Skeleton variant="rounded" width={60} height={24} />
        </Box>
      </Box>
    );
  }

  // 無資料時不顯示
  if (recentHistory.length === 0 && popularHistory.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      {/* 最近搜尋（已登入才顯示） */}
      {isAuthenticated && recentHistory.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 90 }}>
            <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              最近搜尋:
            </Typography>
          </Box>

          {recentHistory.map((item, index) => (
            <Chip
              key={`recent-${item.query}-${index}`}
              label={item.query}
              size="small"
              variant="filled"
              onClick={() => handleQueryClick(item.query)}
              clickable
              sx={{
                backgroundColor: COLORS.recentBg,
                color: COLORS.recentText,
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: COLORS.recentHover,
                },
              }}
            />
          ))}

          <Chip
            icon={<ClearIcon />}
            label={clearing ? '清除中...' : '清除'}
            size="small"
            variant="outlined"
            color="error"
            onClick={handleClearHistory}
            disabled={clearing}
            clickable
            sx={{ ml: 'auto' }}
          />
        </Box>
      )}

      {/* 熱門搜尋 */}
      {popularHistory.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 90 }}>
            <TrendingUpIcon sx={{ fontSize: 18, color: 'warning.main' }} />
            <Typography variant="body2" color="text.secondary">
              熱門搜尋:
            </Typography>
          </Box>

          {popularHistory.map((item, index) => (
            <Chip
              key={`popular-${item.query}-${index}`}
              label={item.query}
              size="small"
              variant="filled"
              onClick={() => handleQueryClick(item.query)}
              clickable
              sx={{
                backgroundColor: COLORS.popularBg,
                color: COLORS.popularText,
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: COLORS.popularHover,
                  transform: 'scale(1.02)',
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

SearchHistory.propTypes = {
  onSelectQuery: PropTypes.func.isRequired,
  limit: PropTypes.number,
};

export default SearchHistory;
